import { defineHotpadsEvent, trackHotpadsEventDefinition } from '../track';
import type { HotpadsCmsInfo } from '../types';

/**
 * CMS block interaction events (HP-9405).
 *
 * These are the Zillow CMS team's registrations, the ones `@zillow/cms-blocks` fires through
 * universal-analytics (4.30.0, `dist/blocks/Button/Button.esm.js`, `Card/Card.esm.js` and
 * `FAQ/FAQ.esm.js`), reused so the CMS dashboards see HotPads activity next to Zillow's.
 * Every id lives in this module: if HotPads registers its own one-to-one copies later, that
 * is a constants change here and nowhere else.
 *
 * Read from the dist, not from the ticket prose:
 * - a button click and a card click are both 2048 on template 126 (`cms_click`); they differ
 *   only in `trigger_source_nm`. 2049 is the `cms_hover` event, which is not sent here.
 * - a FAQ item opening is 5030 on template 297 (`cms_open`); closing is 2051 on template 126
 *   (`cms_close`).
 */

const CMS_INTERACTION_TEMPLATE = { eventTemplateId: '126', eventTemplateVersionId: '1' } as const;

// https://dataportal.zillowgroup.net/event-registry/2048
const CMS_BUTTON_CLICK = defineHotpadsEvent({
  key: 'cms.button_click',
  eventTypeId: '2048',
  eventTypeVersionId: '1',
  ...CMS_INTERACTION_TEMPLATE,
  trigger: {
    type: 'interaction',
    location: 'cms',
    source: 'cms_button',
    object: 'no_trigger_object',
  },
  semanticEvent: 'cms_click',
});

// https://dataportal.zillowgroup.net/event-registry/2048
const CMS_CARD_CLICK = defineHotpadsEvent({
  key: 'cms.card_click',
  eventTypeId: '2048',
  eventTypeVersionId: '1',
  ...CMS_INTERACTION_TEMPLATE,
  trigger: {
    type: 'interaction',
    location: 'cms',
    source: 'cms_card',
    object: 'no_trigger_object',
  },
  semanticEvent: 'cms_click',
});

// https://dataportal.zillowgroup.net/event-registry/5030
const CMS_FAQ_OPEN = defineHotpadsEvent({
  key: 'cms.faq_open',
  eventTypeId: '5030',
  eventTypeVersionId: '1',
  eventTemplateId: '297',
  eventTemplateVersionId: '1',
  trigger: {
    type: 'interaction',
    location: 'cms',
    source: 'cms_faq',
    object: 'no_trigger_object',
  },
  semanticEvent: 'cms_open',
});

// https://dataportal.zillowgroup.net/event-registry/2051
const CMS_FAQ_CLOSE = defineHotpadsEvent({
  key: 'cms.faq_close',
  eventTypeId: '2051',
  eventTypeVersionId: '1',
  ...CMS_INTERACTION_TEMPLATE,
  trigger: {
    type: 'interaction',
    location: 'cms',
    source: 'cms_faq',
    object: 'no_trigger_object',
  },
  semanticEvent: 'cms_close',
});

/** What cms-blocks reports about a clicked button or card. */
export type CmsClickTarget = {
  /** The editor's anchor id of the block; empty when the editor gave it none. */
  blockId: string;
  /** The text the visitor saw: the button label, or the card's text content. */
  displayedText: string;
  /** The sanitized link href; empty when the block has no link. */
  targetUrl: string;
  opensInNewTab: boolean;
};

type CmsFaqItemArg = {
  /** The editor's anchor id of the FAQ item; empty when the editor gave it none. */
  blockId: string;
};

const toClickInfo = (
  blockType: Extract<HotpadsCmsInfo['cms_info']['block_type_cd'], 'button' | 'card'>,
  target: CmsClickTarget,
): HotpadsCmsInfo['cms_info'] => ({
  block_id: target.blockId,
  block_type_cd: blockType,
  block_displayed_txt: target.displayedText,
  block_target_url: target.targetUrl,
  block_target_window_txt: target.opensInNewTab ? '_blank' : '',
});

export function emitTrackCmsButtonClickEvent(target: CmsClickTarget) {
  trackHotpadsEventDefinition(CMS_BUTTON_CLICK, { cmsInfo: toClickInfo('button', target) });
}

export function emitTrackCmsCardClickEvent(target: CmsClickTarget) {
  trackHotpadsEventDefinition(CMS_CARD_CLICK, { cmsInfo: toClickInfo('card', target) });
}

export function emitTrackCmsFaqOpenEvent({ blockId }: CmsFaqItemArg) {
  trackHotpadsEventDefinition(CMS_FAQ_OPEN, { cmsInfo: { block_id: blockId, block_type_cd: 'faq_item' } });
}

export function emitTrackCmsFaqCloseEvent({ blockId }: CmsFaqItemArg) {
  trackHotpadsEventDefinition(CMS_FAQ_CLOSE, { cmsInfo: { block_id: blockId, block_type_cd: 'faq_item' } });
}
